import { has, type Capability, type RoleDef, type TeamMember, type Workspace } from "@omnabase/shared";
import { useSession } from "../shell/store";

/** The signed-in person's default role's capabilities in this workspace (the API's checks without a project read the same). */
export function capsOf(workspace: Workspace): Set<Capability> {
  return new Set((workspace.roles.find((r) => r.id === workspace.me.role)?.capabilities ?? []) as Capability[]);
}
/** What the signed-in person may do in this workspace, from their role's capabilities (the API enforces the same). */
export function useCan(): (cap: string) => boolean {
  const { workspace } = useSession();
  const caps = capsOf(workspace);
  return (cap) => caps.has(cap as Capability);
}

/** the signed-in person as the shared rules read a team member */
const meOf = (workspace: Workspace): TeamMember => ({ id: workspace.me.memberId, status: "active", role: workspace.me.role, projectRoles: workspace.me.projectRoles ?? undefined });
/** May the signed-in person do this on that project? The shared rule with the project, exactly as the API asks it: a project capability
 *  follows the role they hold there (their own rule, else the project's "everyone" role, else their default one). One source for every
 *  screen that checks per project (2026-10-05, DECISIONS "Members in Settings"). */
export function canProject(workspace: Workspace, project: { id: string; everyoneRole?: string | null }, cap: Capability = "projectSettings"): boolean {
  return has(workspace.roles as RoleDef[], meOf(workspace), cap, { id: project.id, everyone: project.everyoneRole ?? null });
}
/** `canProject` for the current workspace */
export function useCanProject(): (project: { id: string; everyoneRole?: string | null }, cap?: Capability) => boolean {
  const { workspace } = useSession();
  return (project, cap) => canProject(workspace, project, cap);
}
